import type { ChunkProgressReporter } from '../shared/chunk-progress';
import type { EAN } from '../shared/ean';
import type { NonEmpty } from '../shared/non-empty';
import type { DecodeResult, DecodeSuccess, Observation, SeriesObservation } from './observation';

// ---------------------------------------------------------------------------
// Cache key types (PRD AC-13)
// ---------------------------------------------------------------------------

export type BrandKey = string & { readonly __brand: 'BrandKey' };
export type BrandCategoryKey = string & { readonly __brand: 'BrandCategoryKey' };

/** Distinguishes real product EANs from b-<slug> and bc-<slug> brand keys. */
export type CacheKey = EAN | BrandKey | BrandCategoryKey;

export function brandKey(slug: string): BrandKey {
  return `b-${slug}` as BrandKey;
}

export function brandCategoryKey(slug: string): BrandCategoryKey {
  return `bc-${slug}` as BrandCategoryKey;
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/**
 * A fully-merged cache entry for one key (EAN or category key).
 * Exposes the typed-decode view.
 * Decode failures have kind:'err' and are surfaced loudly at the repository boundary;
 * siblings always proceed (PRD AC-08).
 */
export type CacheRowResult = {
  readonly upd: string | null;
  readonly observations: readonly DecodeResult[];
  /** True iff the row carried a non-null payload object, including marker-only payloads.
   * False only when no row existed or all rows had a null payl column. */
  readonly hasPayload: boolean;
};

/** The observations of a row that decoded, decode failures dropped. */
export function okObservations(row: CacheRowResult): readonly Observation[] {
  return row.observations
    .filter((r): r is DecodeSuccess => r.kind === 'ok')
    .map((r) => r.observation);
}

/** Per-key batch result. */
export type CacheBatchResult = ReadonlyMap<CacheKey, CacheRowResult>;

/** One row from a bc- brand-category metadata query (no payload). */
export type BrandCategoryMeta = {
  readonly category: string;
  readonly nCount: number;
};

/** One row from a freshness probe (c_key, month and upd only; no payload decode, PRD AC-09). */
export type UpdateProbeEntry = {
  readonly cKey: string;
  /** The row's month partition as `YYYY-MM`, or `'NA'` for a status-marker row. */
  readonly monthYM: string;
  readonly upd: string | null;
};

/**
 * Per-brand decoded observations from a bps- brand-series read (PRD D-05, AC-06).
 * Keys are brand slugs; values are the fully-decoded observations (decodeBrandPayl
 * applied at the repository boundary; decode failures surfaced and dropped).
 */
export type BrandSeriesResult = ReadonlyMap<string, readonly SeriesObservation[]>;

/** A portal_cache query that failed, an aborted one included; `cause` is what PostgREST answered. */
export class ObservationReadError extends Error {
  constructor(
    readonly table: string,
    cause: unknown
  ) {
    super(`[ObservationRepository] ${table} read failed: ${messageOf(cause)}`, { cause });
    this.name = 'ObservationReadError';
  }
}

function messageOf(cause: unknown): string {
  if (typeof cause === 'object' && cause !== null && 'message' in cause) {
    return String((cause as { message: unknown }).message);
  }
  return String(cause);
}

// ---------------------------------------------------------------------------
// Abstract repository
// ---------------------------------------------------------------------------

export abstract class ObservationRepository {
  /**
   * Single-key read with optional month partitioning (y_ref/m_ref OR clause).
   * Date-key merge across rows (last-write-wins per date key).
   * Returns null when no data is found.
   */
  abstract readByKey(key: CacheKey, monthsYM?: readonly string[]): Promise<CacheRowResult | null>;

  /**
   * The `bc-` series of a list of brands under a set of raw category prefixes (each prefix and
   * every path under it), over the given YYYY-MM months, merged additively once per brand and
   * keyed by brand slug; a brand with no rows has no entry. Rejects with an ObservationReadError on a
   * query error; an aborted `signal` ends the read with that rejection.
   */
  abstract readBrandCategorySeries(
    brandSlugs: readonly string[],
    categoryPrefixes: NonEmpty<string>,
    monthsYM: NonEmpty<string>,
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, CacheRowResult>>;

  /**
   * The `b-` series of a list of brands over the given YYYY-MM months, keyed by brand slug with
   * `readByKey`'s merge; a brand with no rows has no entry. Rejects with an ObservationReadError on
   * a query error; an aborted `signal` ends the read with that rejection.
   */
  abstract readWholeBrandSeries(
    brandSlugs: readonly string[],
    monthsYM: NonEmpty<string>,
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, CacheRowResult>>;

  /**
   * Batch read: one row per key, a last-write-wins date-key merge of the key's stored rows in
   * primary-key order (PRD AC-12 part). The result follows the caller's de-duplicated key order
   * and omits keys with no row. An implementation may answer from rows it holds. An aborted
   * `signal` rejects with its reason.
   *
   * `monthsYM` is an ascending, contiguous list of `YYYY-MM` months; `undefined` reads the whole
   * history. A windowed read also matches the `NA` partition (status-marker rows, the only rows
   * with `y_ref = 'NA'`). Under a window, `upd` is the newest `upd` of the rows read, `hasPayload`
   * means the key has rows in those months or a marker row, and a key whose rows all lie outside
   * the months is absent from the result.
   */
  abstract readBatch(
    keys: readonly CacheKey[],
    monthsYM?: NonEmpty<string>,
    onProgress?: ChunkProgressReporter,
    signal?: AbortSignal
  ): Promise<CacheBatchResult>;

  /**
   * Brand-category metadata (`bc-<brandSlug>` rows, category and n_count only, no payload) for a
   * list of brands, grouped by brand slug; a brand with no rows has no
   * entry. Rejects with an ObservationReadError on a query error rather than answering empty; an
   * aborted `signal` ends the read with that rejection.
   */
  abstract loadCategoryMetaOfBrands(
    brandSlugs: readonly string[],
    signal?: AbortSignal
  ): Promise<ReadonlyMap<string, readonly BrandCategoryMeta[]>>;

  /**
   * Freshness probe: one entry per stored row of the given keys, with the row's month partition.
   * Selects only c_key, y_ref, m_ref and upd -- no payload fetch, no decode (PRD AC-09).
   * The refresh service and the campaign dashboard each judge freshness from the returned `upd`.
   * An aborted `signal` rejects with its reason.
   */
  abstract probeUpdates(
    eans: readonly EAN[],
    signal?: AbortSignal
  ): Promise<readonly UpdateProbeEntry[]>;

  /**
   * Brand-series read: fetches the bps-<pbId> row(s) from cacheTable, unwraps
   * the extra brand-slug nesting, and merges month-partitioned rows per brand
   * (last-write-wins date-key, identical to readByKey semantics).
   * The ACL owns the bps- key structure and brand-level nesting (PRD D-05, AC-06).
   * Returns an empty map when no data is found. Rejects with an ObservationReadError on a query
   * error; an aborted `signal` ends the read with that rejection.
   */
  abstract readBrandSeries(
    pbId: string,
    monthsYM?: readonly string[],
    signal?: AbortSignal
  ): Promise<BrandSeriesResult>;

  /** Drops whatever an implementation holds for these keys, so the next read goes to the source. */
  abstract invalidate(keys: readonly CacheKey[]): void;
}
